import { getItem, useAsyncMemo } from "@buzzeasy/shared-frontend-utilities";
import i18next, { Resource } from "i18next";
import { PropsWithChildren, ReactElement, useContext } from "react";
import { I18nextProvider, initReactI18next } from "react-i18next";
import { translations } from "../../translations";
import { agentUITranslationsKey } from "../containers/TranslationCustomizationModal.helpers";
import { translationOverrideLang } from "../containers/UserSettings.helpers";
import { TranslationOverrideContext } from "./TranslationOverrideContext";

const languageLocalStorageKey = "aui-language";

/**
 * Provides the value for AgentUIContext.
 *
 * **Provider dependencies:**
 * - TranslationOverrideProvider
 */
export default function TranslationProvider({ children }: PropsWithChildren): ReactElement {
  const [overrideTranslations] = useContext(TranslationOverrideContext);

  const i18nextInstance = useAsyncMemo(
    async () => {
      const instance = i18next.createInstance();

      const fragmentTranslations = overrideTranslations?.[agentUITranslationsKey];
      const resources = { ...translations, [translationOverrideLang]: { translation: fragmentTranslations ?? {} } } as unknown as Resource;

      await instance
        .use(initReactI18next)
        .init({
          resources,
          preload: Object.keys(translations),
          lng: getItem<string>(languageLocalStorageKey, "en-US"),
          fallbackLng: "en-US",
          returnEmptyString: false,
          interpolation: {
            escapeValue: false,
          },
        });

      return instance;
    },
    [overrideTranslations],
  );

  if (!i18nextInstance)
    return <></>;

  return (
    <I18nextProvider i18n={i18nextInstance}>
      {children}
    </I18nextProvider>
  );
}
